import toast from "react-hot-toast";
import { get } from "lodash";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AttendantType, SingleMoveAttendantPayload } from "routes/group/types";
import { useGetAllGroups } from "routes/group/hooks/useGetAllGroups";
import { Modal } from "components/modal";
import Button from "components/button";
import Select from "components/select";
import { Label } from "components/label";

interface PropType {
  groupId: string; // current group_id
  eventId: string;
  open: boolean;
  loading: boolean;
  attendant: AttendantType | null;
  handleClose: () => void;
  handleSubmitSingleMove: (
    source_group_id: string,
    data: SingleMoveAttendantPayload
  ) => Promise<void>;
}

const SingleMoveModal = (props: PropType) => {
  const {
    groupId,
    eventId,
    open,
    loading,
    attendant,
    handleClose,
    handleSubmitSingleMove,
  } = props;

  const FormSchema = z.object({
    selected_group_id: z.string().min(1, { message: "This field is required" }),
  });

  type FormType = z.infer<typeof FormSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setFocus,
    reset,
  } = useForm<FormType>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      selected_group_id: "",
    },
  });

  const {
    data: groups,
    loading: groupsLoading,
    params: allGroupParams,
    fetchData: fetchAllGroupData,
  } = useGetAllGroups(eventId);

  const GROUP_OPTIONS = useMemo(() => {
    if (!groups) return [];

    return groups
      .filter((g) => g?.id !== groupId)
      .map((g) => ({
        value: g?.id,
        label: g?.group_name_en,
      }));
  }, [groups, groupId]);

  const handleCloseModal = () => {
    reset({ selected_group_id: "" });
    handleClose();
  };

  const handleSubmitForm = (data: any) => {
    console.log("✅ Dữ liệu hợp lệ:", data);

    if (!attendant?.registered_product_id) {
      toast.error("Attendant not found");
      return;
    }

    const payload: SingleMoveAttendantPayload = {
      target_group_id: data?.selected_group_id,
      registered_product_id: attendant?.registered_product_id,
    };

    handleSubmitSingleMove(groupId, payload);
  };

  const onError = (errors: any) => {
    console.error("❌ Lỗi submit:", errors);

    const firstErrorKey = Object.keys(errors)[0];
    setFocus(firstErrorKey as any);
  };

  // all groups
  useEffect(() => {
    if (eventId) fetchAllGroupData(eventId, allGroupParams);
  }, [eventId, JSON.stringify(allGroupParams)]);

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      title={`Move Attendant`}
      closeOnClickBackdrop={false}
    >
      <form
        className="p-5 h-[400px]"
        onSubmit={handleSubmit(handleSubmitForm, onError)}
      >
        <div className="w-full h-full flex flex-col justify-between gap-5">
          <div className="flex flex-col gap-5">
            <div className="w-full flex flex-col gap-1">
              <h3 className="text-center text-[1.125rem] font-semibold mb-3">
                Attendant Information
              </h3>

              <span>
                <span className="font-semibold">Full name:</span>{" "}
                {get(attendant, "full_name", "")}
              </span>

              <span>
                <span className="font-semibold">Email:</span>{" "}
                {get(attendant, "email", "")}
              </span>

              <span>
                <span className="font-semibold">Phone:</span>{" "}
                {get(attendant, "Mobile", "")}
              </span>
            </div>

            <div className="w-full">
              <Controller
                name="selected_group_id"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <div className="w-full flex flex-col gap-1">
                      <Label
                        id={"selected_group_id"}
                        name={"Move to group"}
                        required
                      />

                      <Select
                        options={GROUP_OPTIONS}
                        isClearable={true}
                        error={fieldState.error}
                        id="groups"
                        placeholder="Select option"
                        className="py-0.5"
                        value={GROUP_OPTIONS?.find(
                          (d) => d?.value === field.value
                        )}
                        onChange={(selected: {
                          label: string;
                          value: string;
                        }) => {
                          field.onChange(selected?.value);
                        }}
                      />

                      {fieldState.error && (
                        <span className="error-text font-medium">
                          {fieldState.error.message}
                        </span>
                      )}
                    </div>
                  );
                }}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-x-3">
            <Button
              className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
              onClick={handleCloseModal}
            >
              Cancel
            </Button>

            <Button type="submit" className="w-20" loading={loading}>
              Save
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default SingleMoveModal;
